import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { CalendarDays, Newspaper } from "lucide-react";
import { Seo } from "../components/Seo";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { IMAGES } from "../data/club";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function News() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("All");
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    axios.get(`${API}/news`).then((r) => setPosts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const categories = useMemo(() => ["All", ...new Set(posts.map((p) => p.category))], [posts]);
  const filtered = category === "All" ? posts : posts.filter((p) => p.category === category);

  return (
    <main data-testid="news-page">
      <Seo
        title="Club News — Whitley Bay Cricket Club"
        description="News from West Park: honours, junior cricket, the Active North Tyneside indoor practice grant and the women's team launching in 2027."
      />
      <PageHeader
        eyebrow="From the club"
        title="News &"
        accent="announcements."
        description="Match reports, club news and everything happening at West Park."
        image={IMAGES.sea}
        testId="news-header"
      />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <div className="flex flex-wrap gap-2 mb-10" data-testid="news-filters">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              data-testid={`news-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`text-xs font-bold uppercase tracking-[0.12em] px-4 py-2 rounded-full border transition-colors ${
                category === c
                  ? "bg-[#E5AC3C] text-[#0B1520] border-[#E5AC3C]"
                  : "border-white/15 text-[#9BB0C1] hover:border-[#E5AC3C] hover:text-[#E5AC3C]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {loading && <p className="text-[#5C7285]" data-testid="news-loading">Loading news…</p>}
        {!loading && filtered.length === 0 && <p className="text-[#5C7285]" data-testid="news-empty">No posts in this category yet.</p>}

        <div className="grid gap-6 md:grid-cols-2">
          {filtered.map((n, i) => (
            <Reveal key={n.id} delay={(i % 2) * 0.08}>
              <article
                className="h-full rounded-2xl border border-white/10 bg-[#122030] p-8 hover:border-[#E5AC3C]/40 transition-colors cursor-pointer"
                onClick={() => setExpanded(expanded === n.id ? null : n.id)}
                data-testid={`news-card-${n.id}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="inline-flex items-center gap-2 text-[10px] font-mono-club uppercase tracking-[0.15em] text-[#E5AC3C]">
                    <Newspaper size={12} /> {n.category}
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-xs text-[#5C7285]">
                    <CalendarDays size={12} /> {n.date}
                  </span>
                </div>
                <h2 className="mt-4 font-head text-xl sm:text-2xl font-bold tracking-tight leading-snug">{n.title}</h2>
                <p className="mt-3 text-sm sm:text-base text-[#9BB0C1] leading-relaxed">{n.summary}</p>
                {n.body && (
                  <p className={`mt-3 text-sm text-[#9BB0C1]/80 leading-relaxed ${expanded === n.id ? "" : "line-clamp-2"}`} data-testid={`news-body-${n.id}`}>
                    {n.body}
                  </p>
                )}
                <span className="mt-4 inline-block text-xs font-bold text-[#E5AC3C]" data-testid={`news-toggle-${n.id}`}>
                  {expanded === n.id ? "Show less" : "Read more"}
                </span>
              </article>
            </Reveal>
          ))}
        </div>
      </section>
    </main>
  );
}
